import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { container } from '@/app/container';
import { CatalogRepositoryToken } from '../../di/tokens';
import type {
  ApplyPerkPresetItemBatchInput,
  CreatePerkPresetInput,
} from '../../domain/entities/catalog.entity';

/** Presets paginados — a listagem do catálogo. */
export function usePerkPresets(page = 1, perPage = 10) {
  const repository = container.resolve(CatalogRepositoryToken);
  return useQuery({
    queryKey: ['catalog', 'perk-presets', page, perPage],
    queryFn: () => repository.listPerkPresets({ page, perPage }),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
}

/** Todos os presets, para o seletor de "aplicar preset". */
export function useAllPerkPresets() {
  const repository = container.resolve(CatalogRepositoryToken);
  return useQuery({
    queryKey: ['catalog', 'perk-presets', 'all'],
    queryFn: () => repository.listAllPerkPresets(),
    staleTime: 1000 * 60 * 5,
  });
}

/** Itens do preset — só busca quando há preset escolhido. */
export function usePerkPresetItems(presetId: string) {
  const repository = container.resolve(CatalogRepositoryToken);
  return useQuery({
    queryKey: ['catalog', 'perk-preset-items', presetId],
    queryFn: () => repository.listPerkPresetItems(presetId),
    enabled: presetId.length > 0,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreatePerkPreset() {
  const queryClient = useQueryClient();
  const repository = container.resolve(CatalogRepositoryToken);
  return useMutation({
    mutationFn: (input: CreatePerkPresetInput) =>
      repository.createPerkPreset(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'perk-presets'],
      });
    },
  });
}

/**
 * Renomear e excluir preset. A exclusão leva os itens junto e não mexe em nada
 * já concedido — o preset é clonado no uso, ninguém depende dele depois.
 */
export function usePerkPresetMutations() {
  const queryClient = useQueryClient();
  const repository = container.resolve(CatalogRepositoryToken);
  const invalidate = () =>
    void queryClient.invalidateQueries({
      queryKey: ['catalog', 'perk-presets'],
    });

  const update = useMutation({
    mutationFn: ({
      presetId,
      ...input
    }: CreatePerkPresetInput & { presetId: string }) =>
      repository.updatePerkPreset(presetId, input),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (presetId: string) => repository.deletePerkPreset(presetId),
    onSuccess: invalidate,
  });

  return { update, remove };
}

/**
 * Aplica o diff inteiro dos itens do preset. Invalida também a listagem: um
 * preset editado muda o que a coluna da direita do editor oferece.
 */
export function useApplyPerkPresetItemBatch(presetId: string) {
  const queryClient = useQueryClient();
  const repository = container.resolve(CatalogRepositoryToken);
  return useMutation({
    mutationFn: (input: ApplyPerkPresetItemBatchInput) =>
      repository.applyPerkPresetItemBatch(presetId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'perk-preset-items', presetId],
      });
    },
  });
}
